import { io, Socket } from 'socket.io-client';
import { Message } from '../models/message';
import { Session } from '../models/session';
import type { AiProcessingLogUpdate } from '../models/aiProcessingLog';
import { ConversationLifecycleEvent } from '../features/conversationLifecycle/types';
import { store } from '../redux/store';
import logger from '../utils/logger';
import { SOCKET_URL } from '../config/runtimeUrls';
import { refreshSocketAuthentication } from './socketAuthenticationLifecycle';

type SocketEventHandler = (...args: unknown[]) => void;

export type MessageDeliveryState =
  | 'HELD'
  | 'PENDING'
  | 'CLAIMED'
  | 'UNKNOWN'
  | 'ACCEPTED'
  | 'DELIVERED'
  | 'READ'
  | 'FAILED'
  | 'DEAD_LETTERED';

export interface MessageDeliveryUpdate {
  workerId: string;
  messageId?: string;
  logicalSendKey?: string;
  clientRequestId?: string;
  status: MessageDeliveryState;
}

export type SessionDetailUpdate = Pick<Session, 'workerId'> &
  Partial<
    Pick<
      Session,
      | 'id'
      | 'aiPaused'
      | 'adminIntervention'
      | 'currentQuestionId'
      | 'currentQuestionType'
      | 'language'
      | 'stateVersion'
      | 'takeoverEpoch'
    >
  >;

class SocketService {
  private socket: Socket | null = null;
  private activeListeners: Record<string, { handler: SocketEventHandler, id: number }[]> = {};
  private listenerIdCounter = 0;
  private activeSessionWorkerId: string | null = null;
  private activeSessionId: string | null = null;

  private logListenerChange(action: 'ADDED' | 'REMOVED' | 'REMOVED_ALL', eventName: string, listenerId?: number, removedCount?: number) {
    const totalForEvent = this.activeListeners[eventName]?.length || 0;
    logger.debug('Socket listener registry changed', {
      action,
      eventName,
      listenerId,
      removedCount,
      totalForEvent,
    });
  }

  private addListener(eventName: string, handler: SocketEventHandler) {
    if (!this.socket) {
        logger.warn('Socket is not initialized; listener was not added');
        return;
    }
    if (!this.activeListeners[eventName]) {
        this.activeListeners[eventName] = [];
    }
    const listenerId = ++this.listenerIdCounter;
    this.activeListeners[eventName].push({ handler, id: listenerId });
    this.socket.on(eventName, handler);
    this.logListenerChange('ADDED', eventName, listenerId);
  }

  private removeListener(eventName: string, handler?: SocketEventHandler) {
    if (!this.socket) {
        logger.warn('Socket is not initialized; listener was not removed');
        return;
    }
    if (!this.activeListeners[eventName] || this.activeListeners[eventName].length === 0) {
        // Still call socket.off in case listeners were added outside this tracking (should not happen)
        if (handler) this.socket.off(eventName, handler);
        else this.socket.off(eventName);
        return;
    }

    if (handler) {
        const initialCount = this.activeListeners[eventName].length;
        this.activeListeners[eventName] = this.activeListeners[eventName].filter(l => l.handler !== handler);
        const removedCount = initialCount - this.activeListeners[eventName].length;
        this.socket.off(eventName, handler);
        this.logListenerChange('REMOVED', eventName, undefined, removedCount);
        if (removedCount === 0) {
            logger.warn('Socket listener was not registered during removal');
        }
    } else {
        const removedCount = this.activeListeners[eventName].length;
        this.activeListeners[eventName] = [];
        this.socket.off(eventName);
        this.logListenerChange('REMOVED_ALL', eventName, undefined, removedCount);
    }
  }

  connect(): Socket {
    if (this.socket) {
      return this.socket;
    }

    this.socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'], // Start with polling, then upgrade to websocket
      // path: '/api/socket.io', // Uncomment if your ChatGateway has a specific path
      withCredentials: true, // Add if your ChatGateway cors setup requires it and you handle cookies
      auth: (callback) => callback({ token: store.getState().auth.accessToken ?? '' }),
    });

    this.socket.on('connect', () => {
      logger.debug('Socket connected');
      if (this.activeSessionWorkerId) {
        this.socket?.emit('joinSessionRoom', {
          workerId: this.activeSessionWorkerId,
          sessionId: this.activeSessionId,
          afterCursor: this.activeSessionId ? this.getRememberedCursor(this.activeSessionId) : 0,
        });
      }
    });

    this.socket.on('disconnect', () => {
      logger.debug('Socket disconnected');
    });

    this.socket.on('connect_error', () => {
      logger.error('Socket connection failed');
    });

    return this.socket;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      logger.debug('Socket explicitly disconnected');
      // Clear out tracked listeners on disconnect as they are no longer valid
      this.activeListeners = {}; 
      this.listenerIdCounter = 0; // Reset counter
      this.activeSessionWorkerId = null;
      this.activeSessionId = null;
      logger.debug('Socket listener registry cleared');
    }
  }

  refreshAuthentication(): void {
    // Keep the singleton and its subscriber registry. Reconnecting replaces
    // the server-side rooms and reruns the auth callback with the renewed
    // Redux token; the connect handler then restores only the active room.
    refreshSocketAuthentication(this.socket);
  }

  setActiveSession(workerId: string, sessionId?: string): void {
    this.activeSessionWorkerId = workerId;
    this.activeSessionId = sessionId ?? null;
    this.joinSessionRoom(workerId, sessionId);
  }

  clearActiveSession(workerId: string, sessionId?: string): void {
    if (this.activeSessionWorkerId === workerId && (!sessionId || this.activeSessionId === sessionId)) {
      this.activeSessionWorkerId = null;
      this.activeSessionId = null;
    }
    this.leaveSessionRoom(workerId);
  }

  joinSessionRoom(workerId: string, sessionId?: string): void {
    if (!this.socket || !this.socket.connected) {
      logger.error('Socket is not connected; room join was skipped');
      return;
    }
    const isSameWorker = this.activeSessionWorkerId === workerId;
    this.activeSessionWorkerId = workerId;
    this.activeSessionId = sessionId ?? (isSameWorker ? this.activeSessionId : null);
    this.socket.emit('joinSessionRoom', {
      workerId,
      sessionId: this.activeSessionId,
      afterCursor: this.activeSessionId ? this.getRememberedCursor(this.activeSessionId) : 0,
    });
    logger.debug('Socket room join requested');

    // Optional: Listen for confirmation or errors from the server for joining room
    this.socket.once('joinedSessionRoom', () => {
        logger.debug('Socket room join confirmed');
    });
    this.socket.once('error', () => { // Assuming server emits 'error' for specific issues
        logger.error('Socket room join failed');
    });
  }

  leaveSessionRoom(workerId: string): void {
    if (!this.socket || !this.socket.connected) {
      logger.error('Socket is not connected; room leave was skipped');
      return;
    }
    this.socket.emit('leaveSessionRoom', { workerId });
    logger.debug('Socket room leave requested');

    // Optional: Listen for confirmation
    this.socket.once('leftSessionRoom', () => {
        logger.debug('Socket room leave confirmed');
    });
  }

  onNewMessage(callback: (message: Message) => void): void {
    this.addListener('newMessage', callback);
  }

  offNewMessage(callback?: (message: Message) => void): void {
    this.removeListener('newMessage', callback);
  }

  onSessionListUpdate(callback: (sessionData: Partial<Session>) => void): void {
    this.addListener('sessionListUpdate', callback);
  }

  offSessionListUpdate(callback?: (sessionData: Partial<Session>) => void): void {
    this.removeListener('sessionListUpdate', callback);
  }

  onSessionDetailUpdate(callback: (detailUpdate: SessionDetailUpdate) => void): void {
    this.addListener('sessionDetailUpdate', callback);
  }

  offSessionDetailUpdate(callback?: (detailUpdate: SessionDetailUpdate) => void): void {
    this.removeListener('sessionDetailUpdate', callback);
  }

  onAiLogUpdate(callback: (log: AiProcessingLogUpdate) => void): void {
    this.addListener('aiLogUpdate', callback);
  }

  offAiLogUpdate(callback?: (log: AiProcessingLogUpdate) => void): void {
    this.removeListener('aiLogUpdate', callback);
  }

  onConversationLifecycle(callback: (event: ConversationLifecycleEvent) => void): void {
    this.addListener('conversationLifecycle', callback);
  }

  offConversationLifecycle(callback?: (event: ConversationLifecycleEvent) => void): void {
    this.removeListener('conversationLifecycle', callback);
  }

  onMessageDeliveryUpdate(callback: (update: MessageDeliveryUpdate) => void): void {
    this.addListener('messageDeliveryUpdate', callback);
  }

  offMessageDeliveryUpdate(callback?: (update: MessageDeliveryUpdate) => void): void {
    this.removeListener('messageDeliveryUpdate', callback);
  }

  rememberCursor(sessionId: string, cursor: number): void {
    if (!Number.isSafeInteger(cursor) || cursor < 0) return;
    try {
      sessionStorage.setItem(`optima:conversation-cursor:${sessionId}`, String(cursor));
    } catch {
      logger.warn('Conversation cursor could not be persisted; reconnect will reconcile from zero');
    }
  }

  getRememberedCursor(sessionId: string): number {
    try {
      const value = Number(sessionStorage.getItem(`optima:conversation-cursor:${sessionId}`));
      return Number.isSafeInteger(value) && value >= 0 ? value : 0;
    } catch {
      logger.warn('Conversation cursor could not be read; reconnect will reconcile from zero');
      return 0;
    }
  }

  // ─── v7 cert-ingest pipeline events ─────────────────────────────────────
  // The backend's ChatGateway joins each connected client to ADMIN_CERT_QUEUE_ROOM
  // automatically. Frontend filters by jobId in the callback to figure out
  // which upload completed. See optima-backend/assets/flows/cert-ingestion-job-lifecycle.md.

  onCertUploadCompleted(
    callback: (payload: {
      jobId: string;
      workerId: number;
      certIds: string[];
      failedCertIds: string[];
    }) => void,
  ): void {
    this.addListener('cert_upload_completed', callback);
  }

  offCertUploadCompleted(callback?: (payload: {
    jobId: string;
    workerId: number;
    certIds: string[];
    failedCertIds: string[];
  }) => void): void {
    this.removeListener('cert_upload_completed', callback);
  }

  onCertUploadFailed(
    callback: (payload: {
      jobId: string;
      workerId: number;
      reason: string;
      errorCode?: string;
    }) => void,
  ): void {
    this.addListener('cert_upload_failed', callback);
  }

  offCertUploadFailed(callback?: (payload: {
    jobId: string;
    workerId: number;
    reason: string;
    errorCode?: string;
  }) => void): void {
    this.removeListener('cert_upload_failed', callback);
  }

  onCertSourceUpdated(
    callback: (payload: {
      certId: string;
      workerId: number;
      extractionSucceeded: boolean;
      photoMismatch?: boolean;
    }) => void,
  ): void {
    this.addListener('cert_source_updated', callback);
  }

  offCertSourceUpdated(callback?: (payload: {
    certId: string;
    workerId: number;
    extractionSucceeded: boolean;
    photoMismatch?: boolean;
  }) => void): void {
    this.removeListener('cert_source_updated', callback);
  }

  // Fired when a recruiter confirms/rejects a cert (status changed). Lets the
  // review tab + the worker Files list refresh without a page reload.
  onCertReviewUpdated(
    callback: (payload: { certId: string; workerId: number; status: string }) => void,
  ): void {
    this.addListener('cert_review_updated', callback);
  }

  offCertReviewUpdated(callback?: (payload: { certId: string; workerId: number; status: string }) => void): void {
    this.removeListener('cert_review_updated', callback);
  }

  // ─── Recruiter-triggered CV parse events ───────────────────────────────
  // Fired by the backend's `handleCVParsingComplete` when a parse_history
  // row's source is DASHBOARD. Used by FileParseResultsModal to flip from
  // "Reading CV…" spinner to the Review state without polling.
  // jobId-filtering happens in the callback so concurrent parses don't
  // cross-trigger each other's modals.

  onFileParseCompleted(
    callback: (payload: {
      jobId: string;
      workerId: string;
      success: boolean;
      answeredCount: number;
      totalCount: number;
      errorMessage?: string;
    }) => void,
  ): void {
    this.addListener('fileParseCompleted', callback);
  }

  offFileParseCompleted(callback?: (payload: {
    jobId: string;
    workerId: string;
    success: boolean;
    answeredCount: number;
    totalCount: number;
    errorMessage?: string;
  }) => void): void {
    this.removeListener('fileParseCompleted', callback);
  }

  getSocket(): Socket | null {
    return this.socket;
  }
}

// Export a singleton instance
const socketService = new SocketService();
export default socketService; 
